<template>
  <b-card v-if="render">
    <layout-loading v-if="itemLoading" />
    <b-form>
      <b-row>
        <b-col md="6">
          <b-form-group
            :label="$t('general.title')"
            label-for="question-title"
          >
            <b-form-input
              id="question-title"
              v-model="form.title"
              :state="validateState('title')"
            />
            <b-form-invalid
              id="input-title-feedback"
              :label="$t('general.title')"
              :validator="$v.form.title"
            />
          </b-form-group>
        </b-col>
        <b-col md="6">
          <b-form-group
            :label="$t('general.slug')"
            label-for="question-slug"
          >
            <b-form-input
              id="question-slug"
              v-model="form.slug"
              :state="validateState('slug')"
            />
            <b-form-invalid
              id="input-slug-feedback"
              :label="$t('general.slug')"
              :validator="$v.form.slug"
            />
          </b-form-group>
        </b-col>
        <b-col md="6">
          <b-form-group
            :label="$t('general.language')"
            label-for="question-lang"
          >
            <languageSelector
              v-model="form.lang"
              @change-selected-language="changeSelectedLanguage"
            />
          </b-form-group>
        </b-col>
        <b-col md="6">
          <b-form-group
            :label="$t('general.writer')"
            label-for="question-writer"
            :state="validateState('writer')"
          >
            <v-select-custom
              id="question-writer"
              v-model="form.writer"
              :custom-props="{ label: 'fullName' }"
              :options="userOptions"
              @open="fetchUsersByRole({ role: 'writer' }, form.lang)"
              @search="fetchUsersByRole({role: 'writer', 'search' : $event}, form.lang)"
            />
            <b-form-invalid
              id="input-writerId-feedback"
              :label="$t('general.writer')"
              :validator="$v.form.writer"
              :is-bootstrap="false"
            />
          </b-form-group>
        </b-col>
        <b-col md="6">
          <b-form-group
            :label="$t('general.category')"
            label-for="question-category"
            :state="validateState('category')"
          >
            <v-select-custom
              id="question-category"
              v-model="form.category"
              :options="categoryOptions"
            />
            <b-form-invalid
              id="input-category-feedback"
              :label="$t('general.category')"
              :validator="$v.form.category"
              :is-bootstrap="false"
            />
          </b-form-group>
        </b-col>
        <b-col md="6">
          <b-form-group
            :label="$t('general.tags')"
            label-for="question-tag"
          >
            <v-select-custom
              id="question-tags"
              v-model="form.tags"
              :custom-props="{ multiple: true, taggable: true }"
              :options="tags"
              @search="fetchTags(form.lang, $event)"
              @option:created="addNewTag(form.lang, $event)"
            />
          </b-form-group>
        </b-col>
        <b-col md="6">
          <b-form-group
            :label="$t('general.editor')"
            label-for="question-editor"
          >
            <v-select-custom
              id="question-editor"
              v-model="form.editor"
              :custom-props="{ label: 'fullName' }"
              :options="userOptions"
              @open="fetchUsersByRole({ role: 'editor' }, form.lang)"
              @search="fetchUsersByRole({role: 'editor', 'search' : $event}, form.lang)"
            />
          </b-form-group>
        </b-col>
        <b-col md="6">
          <b-form-group
            :label="$t('general.approver')"
            label-for="article-approver"
          >
            <v-select
              id="question-approver"
              v-model="form.approver"
              label="firstName"
              dir="rtl"
              :options="approverOptions"
              @search="fetchApprovers(form.lang, $event)"
            >
              <template #selected-option="option">
                <v-select-option
                  :value="option.managerImageFileId"
                  :title="option.firstName + option.lastName"
                />
              </template>
              <template
                slot="option"
                slot-scope="option"
              >
                <v-select-option
                  :value="option.managerImageFileId"
                  :title="option.firstName + option.lastName"
                />
              </template>
            </v-select>
          </b-form-group>
        </b-col>
        <b-col md="12">
          <b-form-group
            :label="$t('general.keywords')"
            label-for="question-keywords"
          >
            <b-form-input
              id="question-keywords"
              v-model="form.keywords"
            />
          </b-form-group>
        </b-col>
        <b-col md="12">
          <b-form-group
            :label="$t('general.relatedArticles')"
            label-for="question-related-articles"
            class="mb-2"
            :state="validateState('relatedArticles')"
          >
            <v-select
              id="question-related-articles"
              v-model="form.relatedArticles"
              dir="rtl"
              multiple
              label="title"
              :options="articleOptions"
              @search="fetchArticles(form.lang, $event, 'title')"
            >
              <template #selected-option="option">
                <v-select-option
                  :value="option.articleThumbnailFileId"
                  :title="option.title"
                />
              </template>
              <template
                slot="option"
                slot-scope="option"
              >
                <v-select-option
                  :value="option.articleThumbnailFileId"
                  :title="option.title"
                />
              </template>
            </v-select>
            <b-form-invalid
              id="input-relatedArticles-feedback"
              :label="$t('general.relatedArticles')"
              :validator="$v.form.relatedArticles"
              :is-bootstrap="false"
            />
          </b-form-group>
        </b-col>
        <b-col md="12">
          <b-form-group
            :label="$t('general.description')"
            label-for="question-description"
          >
            <b-form-textarea
              id="question-description"
              v-model="form.description"
              rows="4"
              aria-describedby="input-description-feedback"
            />
          </b-form-group>
        </b-col>
        <b-col md="12">
          <b-form-group
            :label="$t('questions.question')"
            label-for="question-question"
          >
            <b-form-textarea
              id="question-question"
              v-model="form.question"
              :state="validateState('question')"
              rows="4"
              aria-describedby="input-question-feedback"
            />
            <b-form-invalid
              id="input-question-feedback"
              :label="$t('questions.question')"
              :validator="$v.form.question"
            />
          </b-form-group>
        </b-col>
        <b-col
          md="12"
          class="collapse-border"
        >
          <b-form-group
            label-for="question-answer"
            class="mb-2"
          >
            <CkeditorBasic
              id="question-answer"
              v-model="form.answer"
              :label="$t('general.answer')"
              :state="validateState('answer')"
            />
            <b-form-invalid
              id="input-answer-feedback"
              :label="$t('general.answer')"
              :validator="$v.form.answer"
              :is-bootstrap="false"
            />
          </b-form-group>
        </b-col>
        <b-col
          cols="12"
          class="mt-50"
        >
          <b-button
            v-ripple.400="'rgba(255, 255, 255, 0.15)'"
            variant="primary"
            class="mr-1"
            :disabled="isSubmit"
            @click="submitForm"
          >
            {{ isSubmit ? $t("general.isSubmit") : $t("general.submit") }}
          </b-button>
          <b-button
            v-ripple.400="'rgba(186, 191, 199, 0.15)'"
            variant="outline-secondary"
            @click="$router.go(-1)"
          >
            {{ $t("general.back") }}
          </b-button>
        </b-col>
      </b-row>
    </b-form>
  </b-card>
</template>
<script>
import {
  BButton, BCard, BCol, BForm, BFormGroup, BFormInput, BFormTextarea, BRow,
} from 'bootstrap-vue'
import '@/components/BFileInput/index.vue'
import BFormInvalid from '@/components/BFormInvalid/index.vue'
import CkeditorBasic from '@/components/CkeditorBasic/index.vue'
import LayoutLoading from '@/components/LayoutLoading.vue'
import Ripple from 'vue-ripple-directive'
import VSelectCustom from '@/components/VSelectCustom/index.vue'
import VSelectOption from '@/components/VSelectCustom/VSelectOption.vue'
import languageSelector from '@/components/language-selector.vue'
import vSelect from 'vue-select'
import { required, minLength } from 'vuelidate/lib/validators'
import useQuestionsList from './useQuestionsList'

export default {
  name: 'QuestionEdit',
  components: {
    BButton,
    BCard,
    BCol,
    BForm,
    BFormGroup,
    BFormInput,
    BFormInvalid,
    BFormTextarea,
    BRow,
    CkeditorBasic,
    LayoutLoading,
    VSelectCustom,
    VSelectOption,
    languageSelector,
    vSelect,
  },
  directives: {
    Ripple,
  },
  validations() {
    return {
      form: {
        title: {
          required,
          minLength: minLength(3),
        },
        question: {
          required,
          minLength: minLength(10),
        },
        answer: {
          required,
          minLength: minLength(10),
        },
        category: {
          required,
        },
        writer: {
          required,
        },
        slug: {
          required,
          isUnique: () => this.isUniqueSlug,
        },
        relatedArticles: {},
      },
    }
  },
  setup() {
    const {
      addNewTag,
      approverOptions,
      categoryOptions,
      articleOptions,
      convertResource,
      checkSlugIsUnique,
      createTagIdArray,
      errorManager,
      fetchApprovers,
      fetchCategories,
      fetchArticles,
      fetchItem,
      fetchTags,
      fetchUsersByRole,
      form,
      handleUpload,
      isSubmit,
      item,
      itemLoading,
      newTagIds,
      tags,
      updateItem,
      userOptions,
      isUniqueSlug,
    } = useQuestionsList()
    return {
      addNewTag,
      approverOptions,
      categoryOptions,
      articleOptions,
      convertResource,
      checkSlugIsUnique,
      createTagIdArray,
      errorManager,
      fetchApprovers,
      fetchCategories,
      fetchArticles,
      fetchItem,
      fetchTags,
      fetchUsersByRole,
      form,
      handleUpload,
      isSubmit,
      item,
      itemLoading,
      newTagIds,
      tags,
      updateItem,
      userOptions,
      isUniqueSlug,
    }
  },
  data() {
    return {
      editorOptions: [],
      writerOptions: [],
    }
  },
  computed: {
    itemId() {
      return this.$router.currentRoute.params.id
    },
    render() {
      return this.item ? this.item && Object.keys(this.item).length : true
    },
  },
  watch: {
    'form.slug': function () {
      this.checkSlugIsUnique(this.form.lang, this.form.slug)
    },
  },
  created() {
    this.fetchItem(this.itemId).then(() => {
      this.fetchTags(this.form.lang)
      this.fetchArticles(this.form.lang)
      this.fetchUsersByRole({
        role: 'writer',
      }).then(res => {
        this.writerOptions = res
      })
      this.fetchUsersByRole({
        role: 'editor',
      }).then(res => {
        this.editorOptions = res
      })
      this.fetchCategories(this.form.lang, '', '', 'null')
      this.fetchApprovers(this.form.lang)
    })
  },
  methods: {
    changeSelectedLanguage(selectedLang) {
      this.form = this.convertResource(this.item, selectedLang)
      this.fetchCategories(selectedLang)
      this.fetchTags(selectedLang)
      this.fetchArticles(selectedLang)
    },
    validateState(name) {
      const {
        $dirty,
        $error,
      } = this.$v.form[name]
      return $dirty ? !$error : null
    },
    submitForm() {
      this.$v.form.$touch()
      if (this.$v.form.$anyError) {
        this.errorManager('validator.validationError')
        setTimeout(() => document.getElementsByClassName('is-invalid')[0].scrollIntoView({
          behavior: 'smooth',
          block: 'center',
        }), 500)
        return
      }
      this.sendData(this.form)
    },
    sendData(form) {
      const data = {
        title: form.title,
        question: form.question,
        keywords: form.keywords,
        answer: form.answer,
        description: form.description,
        tagIds: this.createTagIdArray(this.form.tags, this.newTagIds),
        editorId: form.editor ? form.editor._id : null,
        articleCategoryId: form.category ? form.category._id : null,
        approverId: form.approver ? form.approver._id : null,
        relatedArticleIds: form.relatedArticles ? form.relatedArticles.map(item => item._id) : [],
        writerId: form.writer ? form.writer._id : null,
        slug: form.slug,
      }
      this.updateItem(this.itemId, data, this.form.lang)
    },
  },
}
</script>
